/**
 * Create Bot Form Component
 * Step-by-step form for creating a new lending bot
 */

import { useState } from 'react'
import type { BotConfig } from '../../services/bots.service'
import { STRATEGY_TEMPLATES, type StrategyTemplate } from '../../config/strategy-templates'
import { BotConfigForm } from './BotConfigForm'
import { SUPPORTED_CURRENCIES, CURRENCY_LABELS } from '../../types/currency'

interface CreateBotFormProps {
  apiKeys: Array<{ id: number; label: string | null }>
  onSubmit: (data: { api_key_id: number; name: string; currency: string; config: BotConfig }) => void
  onCancel: () => void
  isLoading: boolean
}

type FormStep = 'basic' | 'template' | 'config' | 'review'

export function CreateBotForm({ apiKeys, onSubmit, onCancel, isLoading }: CreateBotFormProps) {
  const [currentStep, setCurrentStep] = useState<FormStep>('basic')
  const [selectedTemplate, setSelectedTemplate] = useState<StrategyTemplate | null>(null)
  const [useCustomConfig, setUseCustomConfig] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Basic info
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    api_key_id: apiKeys[0]?.id || 0,
    currency: 'USD',
  })

  // Config (from template or custom)
  const [config, setConfig] = useState<BotConfig>({
    strategy: 'spread',
    min_daily_rate: 0.0002,
    gap_mode: 'relative',
    gap_bottom: 2.0,
    gap_top: 20.0,
    spread_lend: 10,
    min_loan_size: 100,
    max_percent_to_lend: 80.0,
    auto_transfer: false,
    dry_run: false,
    update_interval: 600,
  })

  const handleBasicNext = () => {
    const newErrors: Record<string, string> = {}

    if (!basicInfo.name.trim()) {
      newErrors.name = '請輸入 Bot 名稱'
    } else if (basicInfo.name.length < 2) {
      newErrors.name = 'Bot 名稱至少需要 2 個字元'
    } else if (basicInfo.name.length > 50) {
      newErrors.name = 'Bot 名稱不能超過 50 個字元'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setCurrentStep('template')
  }

  const handleTemplateSelect = (template: StrategyTemplate) => {
    setSelectedTemplate(template)
    setConfig({ ...config, ...template.config })
    setUseCustomConfig(false)
    setErrors({})
  }

  const handleTemplateNext = () => {
    if (!selectedTemplate && !useCustomConfig) {
      setErrors({ template: '請選擇策略模板或自訂配置' })
      return
    }
    setErrors({})
    if (useCustomConfig) {
      setCurrentStep('config')
    } else {
      setCurrentStep('review')
    }
  }

  const handleConfigNext = () => {
    const newErrors: Record<string, string> = {}

    // Validate rate
    if (config.min_daily_rate <= 0) {
      newErrors.min_daily_rate = '最低日利率必須大於 0'
    } else if (config.min_daily_rate > 1) {
      newErrors.min_daily_rate = '最低日利率不能超過 100%'
    } else if (config.min_daily_rate < 0.00001) {
      newErrors.min_daily_rate = '最低日利率太低（建議至少 0.001%）'
    }

    // Validate gap
    if (config.gap_bottom < 0) {
      newErrors.gap_bottom = 'Gap Bottom 不能為負數'
    }
    if (config.gap_top <= config.gap_bottom) {
      newErrors.gap_top = 'Gap Top 必須大於 Gap Bottom'
    }

    // Validate spread
    const spreadLend = config.spread_lend || 10
    if (spreadLend < 1) {
      newErrors.spread_lend = 'Spread Lend 至少需要 1 筆'
    } else if (spreadLend > 100) {
      newErrors.spread_lend = 'Spread Lend 不能超過 100 筆'
    }

    // Validate min loan size
    if (config.min_loan_size && config.min_loan_size < 50) {
      newErrors.min_loan_size = '最小放貸金額建議至少 50'
    }

    // Validate max percent to lend
    if (config.max_percent_to_lend) {
      if (config.max_percent_to_lend < 0) {
        newErrors.max_percent_to_lend = '最大放貸百分比不能為負數'
      } else if (config.max_percent_to_lend > 100) {
        newErrors.max_percent_to_lend = '最大放貸百分比不能超過 100%'
      }
    }

    // Validate update interval
    if (config.update_interval && config.update_interval < 60) {
      newErrors.update_interval = '更新間隔至少需要 60 秒'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setCurrentStep('review')
  }

  const handleSubmit = () => {
    onSubmit({
      api_key_id: basicInfo.api_key_id,
      name: basicInfo.name,
      currency: basicInfo.currency,
      config,
    })
  }

  return (
    <div style={styles.container}>
      {/* Progress Steps */}
      <div style={styles.steps}>
        <StepIndicator label="基本資訊" active={currentStep === 'basic'} completed={currentStep !== 'basic'} />
        <StepIndicator label="策略模板" active={currentStep === 'template'} completed={currentStep === 'config' || currentStep === 'review'} />
        {useCustomConfig && (
          <StepIndicator label="自訂配置" active={currentStep === 'config'} completed={currentStep === 'review'} />
        )}
        <StepIndicator label="確認" active={currentStep === 'review'} completed={false} />
      </div>

      {/* Step Content */}
      <div style={styles.content}>
        {currentStep === 'basic' && (
          <BasicInfoStep
            data={basicInfo}
            apiKeys={apiKeys}
            onChange={setBasicInfo}
            onNext={handleBasicNext}
            onCancel={onCancel}
            errors={errors}
          />
        )}

        {currentStep === 'template' && (
          <TemplateSelectionStep
            templates={STRATEGY_TEMPLATES}
            selectedTemplate={selectedTemplate}
            useCustomConfig={useCustomConfig}
            onSelectTemplate={handleTemplateSelect}
            onUseCustomConfig={() => {
              setUseCustomConfig(true)
              setSelectedTemplate(null)
              setErrors({})
            }}
            onNext={handleTemplateNext}
            onBack={() => setCurrentStep('basic')}
            errors={errors}
          />
        )}

        {currentStep === 'config' && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>自訂配置</h3>
            <p style={styles.stepDescription}>完全自訂 Bot 的放貸策略參數</p>

            <BotConfigForm
              config={config}
              onChange={setConfig}
              errors={errors}
              showActions={false}
            />

            <div style={styles.actions}>
              <button onClick={() => setCurrentStep('template')} style={styles.backButton}>
                上一步
              </button>
              <button onClick={handleConfigNext} style={styles.nextButton}>
                下一步
              </button>
            </div>
          </div>
        )}

        {currentStep === 'review' && (
          <ReviewStep
            basicInfo={basicInfo}
            config={config}
            selectedTemplate={selectedTemplate}
            apiKeys={apiKeys}
            onSubmit={handleSubmit}
            onBack={() => setCurrentStep(useCustomConfig ? 'config' : 'template')}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Step Indicator Component
 */
function StepIndicator({ label, active, completed }: { label: string; active: boolean; completed: boolean }) {
  return (
    <div style={styles.stepIndicator}>
      <div
        style={{
          ...styles.stepCircle,
          ...(active ? styles.stepCircleActive : {}),
          ...(completed ? styles.stepCircleCompleted : {}),
        }}
      >
        {completed ? '✓' : ''}
      </div>
      <span style={{ ...styles.stepLabel, ...(active ? styles.stepLabelActive : {}) }}>{label}</span>
    </div>
  )
}

/**
 * Error Message Component
 */
function ErrorMessage({ message }: { message: string }) {
  return (
    <div style={styles.errorMessage}>
      <span style={styles.errorIcon}>⚠️</span>
      <span style={styles.errorText}>{message}</span>
    </div>
  )
}

/**
 * Step 1: Basic Info
 */
function BasicInfoStep({
  data,
  apiKeys,
  onChange,
  onNext,
  onCancel,
  errors,
}: {
  data: { name: string; api_key_id: number; currency: string }
  apiKeys: Array<{ id: number; label: string | null }>
  onChange: (data: any) => void
  onNext: () => void
  onCancel: () => void
  errors: Record<string, string>
}) {
  return (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>基本資訊</h3>
      <p style={styles.stepDescription}>設定 Bot 的基本資訊</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>
          Bot 名稱 <span style={styles.required}>*</span>
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
          placeholder="例如：USD 保守型放貸"
          style={{
            ...styles.input,
            ...(errors.name ? styles.inputError : {}),
          }}
          autoFocus
        />
        {errors.name && <ErrorMessage message={errors.name} />}
        {!errors.name && <span style={styles.helpText}>為您的 Bot 取一個容易識別的名稱</span>}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>
          Bitfinex API Key <span style={styles.required}>*</span>
        </label>
        <select
          value={data.api_key_id}
          onChange={(e) => onChange({ ...data, api_key_id: parseInt(e.target.value) })}
          style={styles.select}
        >
          {apiKeys.map((key) => (
            <option key={key.id} value={key.id}>
              {key.label || `API Key #${key.id}`}
            </option>
          ))}
        </select>
        <span style={styles.helpText}>選擇用於此 Bot 的 API 憑證</span>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>
          幣種 <span style={styles.required}>*</span>
        </label>
        <select
          value={data.currency}
          onChange={(e) => onChange({ ...data, currency: e.target.value })}
          style={styles.select}
        >
          {SUPPORTED_CURRENCIES.map((currency) => (
            <option key={currency} value={currency}>
              {CURRENCY_LABELS[currency]}
            </option>
          ))}
        </select>
        <span style={styles.helpText}>選擇要放貸的幣種</span>
      </div>

      <div style={styles.actions}>
        <button onClick={onCancel} style={styles.cancelButton}>
          取消
        </button>
        <button onClick={onNext} style={styles.nextButton}>
          下一步
        </button>
      </div>
    </div>
  )
}

/**
 * Step 2: Template Selection
 */
function TemplateSelectionStep({
  templates,
  selectedTemplate,
  useCustomConfig,
  onSelectTemplate,
  onUseCustomConfig,
  onNext,
  onBack,
  errors,
}: {
  templates: StrategyTemplate[]
  selectedTemplate: StrategyTemplate | null
  useCustomConfig: boolean
  onSelectTemplate: (template: StrategyTemplate) => void
  onUseCustomConfig: () => void
  onNext: () => void
  onBack: () => void
  errors: Record<string, string>
}) {
  const riskColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
  }

  const riskLabels = {
    low: '低風險',
    medium: '中風險',
    high: '高風險',
  }

  return (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>選擇策略模板</h3>
      <p style={styles.stepDescription}>選擇預設模板或自訂配置</p>

      <div style={styles.templatesGrid}>
        {templates.map((template) => (
          <div
            key={template.id}
            onClick={() => onSelectTemplate(template)}
            style={{
              ...styles.templateCard,
              ...(selectedTemplate?.id === template.id ? styles.templateCardSelected : {}),
            }}
          >
            <div style={styles.templateHeader}>
              <h4 style={styles.templateName}>{template.name}</h4>
              <span style={{ ...styles.riskBadge, backgroundColor: riskColors[template.risk_level] }}>
                {riskLabels[template.risk_level]}
              </span>
            </div>
            <p style={styles.templateDescription}>{template.description}</p>
            <div style={styles.templateDetails}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>預期年化:</span>
                <span style={styles.detailValue}>{template.expected_apr}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>最低利率:</span>
                <span style={styles.detailValue}>
                  {(template.config.min_daily_rate * 100).toFixed(4)}%/天
                </span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>分散度:</span>
                <span style={styles.detailValue}>{template.config.spread_lend} 筆訂單</span>
              </div>
            </div>
            <p style={styles.templateSuitable}>{template.suitable_for}</p>
          </div>
        ))}
      </div>

      <div style={styles.customOption}>
        <button
          onClick={onUseCustomConfig}
          style={{
            ...styles.customButton,
            ...(useCustomConfig ? styles.customButtonSelected : {}),
          }}
        >
          {useCustomConfig ? '✓ ' : ''}自訂配置
        </button>
        <p style={styles.customDescription}>進階用戶可以完全自訂所有參數</p>
      </div>

      {errors.template && <ErrorMessage message={errors.template} />}

      <div style={styles.actions}>
        <button onClick={onBack} style={styles.backButton}>
          上一步
        </button>
        <button onClick={onNext} style={styles.nextButton}>
          下一步
        </button>
      </div>
    </div>
  )
}


/**
 * Step 4: Review
 */
function ReviewStep({
  basicInfo,
  config,
  selectedTemplate,
  apiKeys,
  onSubmit,
  onBack,
  isLoading,
}: {
  basicInfo: { name: string; api_key_id: number; currency: string }
  config: BotConfig
  selectedTemplate: StrategyTemplate | null
  apiKeys: Array<{ id: number; label: string | null }>
  onSubmit: () => void
  onBack: () => void
  isLoading: boolean
}) {
  const apiKeyLabel = apiKeys.find((k) => k.id === basicInfo.api_key_id)?.label || `API Key #${basicInfo.api_key_id}`

  return (
    <div style={styles.stepContent}>
      <h3 style={styles.stepTitle}>確認配置</h3>
      <p style={styles.stepDescription}>請確認以下設定無誤後建立 Bot</p>

      <div style={styles.reviewSection}>
        <h4 style={styles.reviewSectionTitle}>基本資訊</h4>
        <div style={styles.reviewGrid}>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>Bot 名稱:</span>
            <span style={styles.reviewValue}>{basicInfo.name}</span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>API Key:</span>
            <span style={styles.reviewValue}>{apiKeyLabel}</span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>幣種:</span>
            <span style={styles.reviewValue}>{basicInfo.currency}</span>
          </div>
        </div>
      </div>

      {selectedTemplate && (
        <div style={styles.reviewSection}>
          <h4 style={styles.reviewSectionTitle}>策略模板</h4>
          <div style={styles.templatePreview}>
            <span style={styles.templatePreviewName}>{selectedTemplate.name}</span>
            <span style={styles.templatePreviewDesc}>{selectedTemplate.description}</span>
            <span style={styles.templatePreviewAPR}>預期年化: {selectedTemplate.expected_apr}</span>
          </div>
        </div>
      )}

      <div style={styles.reviewSection}>
        <h4 style={styles.reviewSectionTitle}>配置參數</h4>
        <div style={styles.reviewGrid}>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>最低日利率:</span>
            <span style={styles.reviewValue}>
              {(config.min_daily_rate * 100).toFixed(4)}%
              <span style={styles.reviewHint}> (≈{(config.min_daily_rate * 365 * 100).toFixed(2)}% APR)</span>
            </span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>Gap Mode:</span>
            <span style={styles.reviewValue}>{config.gap_mode}</span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>Gap 範圍:</span>
            <span style={styles.reviewValue}>
              {config.gap_bottom} ~ {config.gap_top}
            </span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>分散筆數:</span>
            <span style={styles.reviewValue}>{config.spread_lend} 筆</span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>最小放貸金額:</span>
            <span style={styles.reviewValue}>{config.min_loan_size}</span>
          </div>
          {config.max_percent_to_lend && (
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>最大放貸比例:</span>
              <span style={styles.reviewValue}>{config.max_percent_to_lend}%</span>
            </div>
          )}
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>自動轉帳:</span>
            <span style={styles.reviewValue}>{config.auto_transfer ? '啟用' : '停用'}</span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>測試模式:</span>
            <span style={styles.reviewValue}>{config.dry_run ? '啟用' : '停用'}</span>
          </div>
          <div style={styles.reviewItem}>
            <span style={styles.reviewLabel}>更新間隔:</span>
            <span style={styles.reviewValue}>{config.update_interval} 秒</span>
          </div>
        </div>
      </div>

      <div style={styles.actions}>
        <button onClick={onBack} style={styles.backButton} disabled={isLoading}>
          上一步
        </button>
        <button onClick={onSubmit} style={styles.submitButton} disabled={isLoading}>
          {isLoading ? '建立中...' : '確認建立'}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Styles
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '20px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  steps: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginBottom: '40px',
    padding: '20px 0',
    borderBottom: '1px solid #e5e7eb',
  },
  stepIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  stepCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '14px',
  },
  stepCircleActive: {
    backgroundColor: '#2563eb',
    color: 'white',
  },
  stepCircleCompleted: {
    backgroundColor: '#10b981',
    color: 'white',
  },
  stepLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500',
  },
  stepLabelActive: {
    color: '#2563eb',
    fontWeight: '600',
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '30px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  stepContent: {
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
  },
  stepTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  stepDescription: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '30px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '8px',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  helpText: {
    display: 'block',
    fontSize: '12px',
    color: '#666',
    marginTop: '6px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: 'auto',
    paddingTop: '30px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  backButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  nextButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '12px 32px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  templatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '30px',
  },
  templateCard: {
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  templateCardSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  templateName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
  },
  riskBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    color: 'white',
    fontWeight: '600',
  },
  templateDescription: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '16px',
  },
  templateDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
  },
  detailLabel: {
    color: '#666',
  },
  detailValue: {
    fontWeight: '600',
    color: '#333',
  },
  templateSuitable: {
    fontSize: '11px',
    color: '#999',
    fontStyle: 'italic',
    marginTop: '8px',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
  },
  customOption: {
    textAlign: 'center',
    padding: '20px',
    marginBottom: '20px',
  },
  customButton: {
    padding: '12px 32px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: '2px solid #e5e7eb',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  customButtonSelected: {
    backgroundColor: '#eff6ff',
    borderColor: '#2563eb',
    color: '#2563eb',
  },
  customDescription: {
    fontSize: '12px',
    color: '#666',
    marginTop: '8px',
  },
  reviewSection: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  reviewSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
  },
  reviewGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  reviewItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  reviewLabel: {
    fontSize: '12px',
    color: '#666',
  },
  reviewValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  reviewHint: {
    fontSize: '12px',
    fontWeight: 'normal',
    color: '#999',
  },
  templatePreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  templatePreviewName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2563eb',
  },
  templatePreviewDesc: {
    fontSize: '13px',
    color: '#666',
  },
  templatePreviewAPR: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#10b981',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '5px',
    marginTop: '8px',
    marginBottom: '16px',
  },
  errorIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  errorText: {
    fontSize: '13px',
    color: '#dc2626',
    fontWeight: '500',
  },
  inputError: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
}
